import { useState, useEffect, Dispatch, FC, SetStateAction } from 'react'
import { ILinkComponentProps } from '../Navbar'
import Dropdown from '../../Dropdown/Dropdown'
import './NavItem.css'

export interface INavItem {
  name: string
  href: string
  subLinks?: INavItem[]
  icon?: string
}

export interface INavItemProps {
  navItem: INavItem
  LinkComponent: FC<ILinkComponentProps>
  activeDropdownIndex: number | null
  setActiveDropdownIndex: Dispatch<SetStateAction<number | null>>
  index: number
  setTimeoutFunction: () => void
  currentDropdownTimeoutId?: number
}

const NavItem = ({
  navItem,
  LinkComponent,
  activeDropdownIndex,
  setActiveDropdownIndex,
  index,
  setTimeoutFunction,
  currentDropdownTimeoutId,
}: INavItemProps) => {
  const [delayDropdownClose, toggleDelayDropdownClose] = useState(false)

  const { name, href, subLinks } = navItem

  const checkActiveItem = () => {
    const isBrowser = typeof window !== 'undefined'
    if (isBrowser) {
      const currentPath = window.location.pathname
      return currentPath.startsWith(href) ||
        subLinks?.filter((el) => {
          return el.href === currentPath
        }).length ||
        (href === '/blog/?page=1' && currentPath.startsWith('/blog'))
        ? 'active'
        : ''
    }
  }

  // Custom useCloseOnEscape for the dropdowns
  useEffect(() => {
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        setActiveDropdownIndex(null)
      }
    })
  }, [activeDropdownIndex])

  const handleMouseLeave = () => {
    if (delayDropdownClose) {
      setTimeoutFunction()
    } else {
      setActiveDropdownIndex(null)
    }
  }

  const handleMouseEnter = () => {
    toggleDelayDropdownClose(false)
    setActiveDropdownIndex(index)
  }

  return (
    <li
      key={name}
      className={`${checkActiveItem()} Navbar__item`}
      data-testid={`nav-item-${index}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className="flex h-full items-center"
        onMouseLeave={() => toggleDelayDropdownClose(false)}
      >
        <LinkComponent href={href} aria-label={name} addClass="Navbar__link">
          <span className="flex items-center">{name}</span>
        </LinkComponent>
        {subLinks && (
          <button
            aria-expanded={index === activeDropdownIndex}
            aria-label={
              index === activeDropdownIndex
                ? `close ${name} sub-menu`
                : `open ${name} sub-menu`
            }
            className={`Navbar__dropdownButton ${
              index === activeDropdownIndex
                ? 'Navbar__dropdownButton--open'
                : ''
            }`}
            data-testid="navbar-button-dropdown"
            onClick={() =>
              setActiveDropdownIndex(
                activeDropdownIndex === index ? null : index
              )
            }
          >
            <i className="bx bx-chevron-down" />
          </button>
        )}
      </div>
      {subLinks && index === activeDropdownIndex && (
        <div
          className="absolute top-0"
          onMouseEnter={() => {
            toggleDelayDropdownClose(true)
            clearTimeout(currentDropdownTimeoutId)
          }}
        >
          <Dropdown
            dropdownItems={subLinks}
            LinkComponent={LinkComponent}
            addClass="Navbar__dropdown"
            data-testid="navbar-dropdown"
          />
        </div>
      )}
    </li>
  )
}

export default NavItem
